import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Mail, CheckCircle2, Users } from "lucide-react";
import FXBackdrop from "../components/FXBackdrop";
import { useAuth } from "../contexts/AuthContext";

const fade = {
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.1 },
    transition: { duration: 0.6 },
};
const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Company dashboard / landing page after onboarding.
 *
 * Intentionally static: no matching API call, no AI framing.
 * The backend matching pipeline is still in build — instead of surfacing a
 * half-working feature, we tell the company their application is in and our
 * team is actively sourcing candidates for them.
 */
export default function MatchResults() {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Route protection + server-side onboarding gate
    useEffect(() => {
        if (authLoading) return;
        if (!isAuthenticated) {
            navigate("/login", { replace: true });
            return;
        }
        if (user?.role !== "company") {
            navigate("/", { replace: true });
            return;
        }

        // Check onboarding status — if incomplete, push them through onboarding.
        async function check() {
            try {
                const token = localStorage.getItem("hirrd_token");
                const res = await fetch(`${API}/api/company/onboarding/status`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    const data = await res.json();
                    if (!data.completed) {
                        navigate("/company/onboarding", { replace: true });
                    }
                }
            } catch {
                // Silent fail — stay on this page and show the sourcing state.
            }
        }
        check();
    }, [authLoading, isAuthenticated, user, navigate]);

    if (authLoading || !user) return null;

    const companyName = user.profile?.company_name || "Partner";

    return (
        <FXBackdrop>
            <section className="containerX pt-10 md:pt-16 pb-16 flex items-center justify-center min-h-[calc(100vh-100px)]">
                <motion.div {...fade} className="w-full max-w-3xl">
                    <div className="glass p-8 md:p-14 relative overflow-hidden text-center rounded-3xl">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400 opacity-10 blur-3xl rounded-full transform translate-x-1/3 -translate-y-1/3 pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-400 opacity-10 blur-3xl rounded-full transform -translate-x-1/3 translate-y-1/3 pointer-events-none" />

                        <div className="relative z-10 flex flex-col items-center">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-50/80 backdrop-blur-sm text-cyan-700 text-xs font-semibold mb-6 border border-cyan-100 shadow-sm">
                                <Search size={14} /> STATUS: SOURCING CANDIDATES
                            </div>

                            <h1 className="h2 text-3xl md:text-5xl font-bold mb-4 tracking-tight">
                                Welcome to hirrd, <br />
                                <span className="text-violet-600 font-extrabold">{companyName}</span>
                            </h1>

                            <p className="text-slate-600 text-lg md:text-xl mb-10 max-w-xl mx-auto leading-relaxed">
                                Thanks for joining hirrd. Our team has received your company
                                details and is actively working to find the best engineers for
                                your stack and culture.
                            </p>

                            <div className="grid md:grid-cols-2 gap-4 w-full max-w-2xl text-left bg-white/40 backdrop-blur border border-white/60 p-6 rounded-2xl shadow-sm">
                                <div className="flex items-start gap-4">
                                    <div className="h-10 w-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                                        <CheckCircle2 size={20} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-900">1. Review</div>
                                        <div className="text-sm text-slate-500 mt-1">
                                            Our team reviews your company profile to understand
                                            your hiring needs.
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="h-10 w-10 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                                        <Mail size={20} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-900">2. Shortlist</div>
                                        <div className="text-sm text-slate-500 mt-1">
                                            We'll email{" "}
                                            <span className="font-medium text-slate-700">
                                                {user?.email}
                                            </span>{" "}
                                            as soon as a curated shortlist is ready.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex items-center gap-2 text-xs text-slate-400">
                                <Users size={12} />
                                Typical turnaround is a few business days.
                            </div>
                        </div>
                    </div>
                </motion.div>
            </section>
        </FXBackdrop>
    );
}
